import { useState } from "react";
import { Link } from "react-router-dom";
import { faqs } from "@/mocks/faqs";
import usePageSeo from "@/hooks/usePageSeo";
import Breadcrumbs from "@/components/feature/Breadcrumbs";

const ORIGIN = "https://houaiw.cn";

export default function FaqPage() {
  const [openIndex, setOpenIndex] = useState<number | null>(0);

  usePageSeo({
    title: "常见问题 - 瓜田吃瓜使用说明与社区规则",
    description:
      "关于瓜田吃瓜的定位、话题榜排序依据、讨论区筛选规则、内容更新频率与投稿方式的常见问题解答，帮助你快速了解这里的内容是怎么来的。",
    keywords: "瓜田吃瓜常见问题,话题榜排序,讨论区规则,社区规范",
    path: "/faq",
    jsonLd: [
      {
        "@context": "https://schema.org",
        "@type": "FAQPage",
        mainEntity: faqs.map((item) => ({
          "@type": "Question",
          name: item.q,
          acceptedAnswer: {
            "@type": "Answer",
            text: item.a,
          },
        })),
      },
      {
        "@context": "https://schema.org",
        "@type": "BreadcrumbList",
        itemListElement: [
          { "@type": "ListItem", position: 1, name: "首页", item: `${ORIGIN}/` },
          { "@type": "ListItem", position: 2, name: "常见问题", item: `${ORIGIN}/faq` },
        ],
      },
    ],
  });

  const toggle = (index: number) => {
    setOpenIndex((current) => (current === index ? null : index));
  };

  return (
    <div className="w-full">
      <section className="w-full bg-background-200/50 border-b border-background-200/70">
        <div className="w-full px-4 md:px-8 py-8 md:py-12">
          <Breadcrumbs items={[{ label: "常见问题" }]} />
          <div className="mt-6 max-w-3xl">
            <span className="inline-flex items-center gap-2 text-xs tracking-[0.22em] uppercase text-secondary-700 mb-3">
              <span className="w-6 h-px bg-secondary-400" />
              FAQ
            </span>
            <h1 className="text-3xl md:text-5xl font-bold text-foreground-950 leading-tight">
              常见问题
            </h1>
            <p className="mt-4 text-sm md:text-base leading-relaxed text-foreground-600">
              关于内容来源、榜单规则和讨论区规范，这里集中回答了被问得最多的几个问题。没找到答案的话，欢迎直接发邮件给我们。
            </p>
          </div>
        </div>
      </section>

      <section className="w-full px-4 md:px-8 py-10 md:py-14">
        <div className="grid grid-cols-1 lg:grid-cols-3 gap-8 lg:gap-12">
          <div className="lg:col-span-2">
            <div className="flex flex-col gap-3">
              {faqs.map((item, index) => {
                const isOpen = openIndex === index;
                return (
                  <article
                    key={item.q}
                    className="rounded-lg bg-background-50 border border-background-200/80 overflow-hidden"
                  >
                    <h2>
                      <button
                        type="button"
                        onClick={() => toggle(index)}
                        aria-expanded={isOpen}
                        className="w-full flex items-start justify-between gap-4 text-left px-5 py-4 hover:bg-background-200/50 transition-colors"
                      >
                        <span className="text-sm md:text-base font-medium text-foreground-950">
                          {item.q}
                        </span>
                        <span
                          className={`shrink-0 w-7 h-7 flex items-center justify-center rounded-full border transition-colors ${
                            isOpen
                              ? "bg-primary-500 text-background-50 border-primary-500"
                              : "border-background-300 text-foreground-500"
                          }`}
                        >
                          <i className={`text-base ${isOpen ? "ri-subtract-line" : "ri-add-line"}`} />
                        </span>
                      </button>
                    </h2>
                    {isOpen && (
                      <div className="px-5 pb-5 -mt-1">
                        <p className="text-sm leading-relaxed text-foreground-600">{item.a}</p>
                      </div>
                    )}
                  </article>
                );
              })}
            </div>
          </div>

          <aside className="flex flex-col gap-6">
            <div className="rounded-lg bg-accent-50 border border-accent-200/70 p-5">
              <h2 className="text-base font-bold text-accent-950">还没解决？</h2>
              <p className="mt-2 text-sm leading-relaxed text-accent-900/80">
                把具体问题发到我们的邮箱，涉及话题内容纠错的，请附上可查证的来源链接。
              </p>
              <a
                href="mailto:hello@guatianchigua.cn"
                className="mt-4 inline-flex items-center gap-2 px-5 py-2.5 rounded-full bg-accent-500 text-background-50 text-sm hover:bg-accent-600 transition-colors whitespace-nowrap"
              >
                <i className="ri-mail-line w-4 h-4 flex items-center justify-center" />
                发送邮件
              </a>
            </div>

            <div className="rounded-lg bg-background-200/50 border border-background-200/70 p-5">
              <h2 className="text-base font-bold text-foreground-950">也许你还想看</h2>
              <div className="mt-4 flex flex-col gap-2">
                <Link
                  to="/topics"
                  className="flex items-center justify-between gap-3 px-4 py-3 rounded-lg bg-background-50 border border-background-200/70 hover:border-primary-300 transition-colors"
                >
                  <span className="text-sm text-foreground-800">今日话题榜</span>
                  <i className="ri-arrow-right-line text-foreground-500" />
                </Link>
                <Link
                  to="/discussion"
                  className="flex items-center justify-between gap-3 px-4 py-3 rounded-lg bg-background-50 border border-background-200/70 hover:border-primary-300 transition-colors"
                >
                  <span className="text-sm text-foreground-800">讨论区观点交锋</span>
                  <i className="ri-arrow-right-line text-foreground-500" />
                </Link>
                <Link
                  to="/about"
                  className="flex items-center justify-between gap-3 px-4 py-3 rounded-lg bg-background-50 border border-background-200/70 hover:border-primary-300 transition-colors"
                >
                  <span className="text-sm text-foreground-800">关于我们与内容原则</span>
                  <i className="ri-arrow-right-line text-foreground-500" />
                </Link>
              </div>
            </div>
          </aside>
        </div>
      </section>
    </div>
  );
}